/* eslint-disable camelcase */
import * as React from "react"
import { useCallback, useMemo } from "react"
import * as yup from "yup"
import moment from "moment"
import { Appearance, TextInput, ViewStyle } from "react-native"
import { Cycle, Disease, Farm } from "../../../database"
import { DateTimePickerField } from "../../field/date-time-picker-field/date-time-picker-field"
import { FormProps } from "../form"
import { Formik } from "formik"
import { ModelField } from "../../field/model-field/model-field"
import { MultipleImageField } from "../../field/multiple-image-field/multiple-image-field"
import { Text } from "../../text/text"
import { TextField } from "../../field/text-field/text-field"
import { View } from "native-base"
import { color } from "../../../theme"
import { observer } from "mobx-react-lite"
import { translate } from "../../../i18n"
import { WalkthroughElement } from "../../walkthrough-element/walkthrough-element"

const TITLE: ViewStyle = {
  borderTopWidth: 1,
  borderColor: color.palette.background,
  paddingHorizontal: 16,
  paddingTop: 16,
  paddingBottom: 10
}

const BORDER: ViewStyle = {
  backgroundColor: color.palette.background,
  height: 4
}

const WRAPPER_IMAGE: ViewStyle = {
  backgroundColor: color.palette.white
}

export interface CycleDiseaseFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    farm_id?: number
    cycle_id?: number
    logged_at?: string
    logged_date?: string
    logged_time?: string
    disease_id?: number
    other_disease?: string
    remark?: string
    files?: Array<any>
  }
}

/**
 * Describe your component here
 */


export const CycleDiseaseForm = observer(function CycleDiseaseForm(props: CycleDiseaseFormProps) {
  const {
    initialValues: initialValuesProps = {},
    onSubmit: onSubmitProps,
    colorScheme = Appearance.getColorScheme(),
    innerRef,
    parentScrollViewRef: scrollViewRef,
    ...rest
  } = props

  const input = {
    disease: React.useRef<TextInput>(),
    remark: React.useRef<TextInput>()
  }

  const nextInput = {
    disease: () => input.remark.current && input.remark.current.focus()
  }

  const onSubmit = useCallback((values) => {
    onSubmitProps({
      ...values,
      files: values.files.map((f, i) => {
        const initialValueFile = (initialValuesProps.files || [])[i]
        const previousUri = ((initialValuesProps.files || [])[i] || {}).path || null
        const uri = (f || {}).uri || null
        if (uri !== previousUri || (!uri && initialValueFile)) {
          if (uri)
            return {
              ...initialValueFile,
              fileable_type: 'App\\CycleDisease',
              title: f.fileName,
              type: f.type,
              size: f.fileSize,
              path: uri
            }
          else if (!uri && initialValueFile?.id) // Delete
            return {
              _action: 'delete',
              ...initialValueFile,
            }
        }
        return initialValueFile
      }),
      logged_at: values.logged_date
        && values.logged_time
        && moment(`${values.logged_date} ${values.logged_time}`).utc().format("YYYY-MM-DD HH:mm:ss")
        || null,
      get logged_date() {
        return this.logged_at && moment.utc(this.logged_at).format("YYYY-MM-DD")
      },
      get logged_time() {
        return this.logged_at && moment.utc(this.logged_at).format("HH:mm:ss")
      },
    })
  }, [initialValuesProps.files, onSubmitProps])

  const validationSchema = yup.object({
    farm_id: yup.number().nullable().required().label(translate("cycleDiseaseForm.farmId")),
    cycle_id: yup.number().nullable().required().label(translate("cycleDiseaseForm.cycleId")),
    logged_date: yup.string().nullable().required().label(translate("cycleDiseaseForm.loggedDate")),
    disease_id: yup.number().nullable()
      .when({ is: v => !!v, then: yup.number().nullable().required() })
      .label(translate("cycleDiseaseForm.diseaseId")),
    other_disease: yup.string().ascii().nullable()
      .when('disease_id', { is: v => !v, then: yup.string().nullable().required() })
      .label(translate("cycleDiseaseForm.otherDisease")),
    files: yup.array().label(translate("cycleDiseaseForm.files")),
    remark: yup.string().ascii().max(255).nullable().label(translate("cycleDiseaseForm.remark")),
  })

  const initialValues = useMemo<CycleDiseaseFormProps["initialValues"]>(() => ({
    farm_id: null,
    ...initialValuesProps,
    files: [null, null, null].map((f, i) => (
      (initialValuesProps.files || [])[i] || {}).path_source || f
    ),
    logged_at: initialValuesProps.logged_at || moment.utc().startOf("minute").format("YYYY-MM-DD HH:mm:ss"),
    get logged_date() {
      return this.logged_at && moment.utc(this.logged_at).local().format("YYYY-MM-DD")
    },
    get logged_time() {
      return this.logged_at && moment.utc(this.logged_at).local().format("HH:mm:ss")
    },
  }), [initialValuesProps])

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ initialValues, values, setFieldValue, setFieldTouched }) => (
        <>
          <ModelField
            name="farm_id"
            txPrefix="cycleDiseaseForm"
            colorScheme={colorScheme}
            formControlProps={{ isRequired: true }}
            modelPickerProps={{
              Model: Farm,
              isDisabled: !!initialValues.farm_id,
              onValueChange: async (value) => {
                await setFieldValue("cycle_id", null)
                await setFieldValue("farm_id", value || null)
                await setFieldTouched("farm_id")
              },
            }}
          />
          <WalkthroughElement
            id="cycle-disease-form-first"
            scrollViewRef={scrollViewRef}
            scrollOffset={250}
          >
            <View style={TITLE}>
              <Text tx="cycleDiseaseForm.cycleDisease" preset="title3" colorScheme={colorScheme} />
            </View>
            <View style={BORDER} />
            <ModelField
              key={`${values.farm_id}-cycle_id`}
              name="cycle_id"
              txPrefix="cycleDiseaseForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
              modelPickerProps={{
                Model: Cycle,
                dataLabel: "pond_name",
                fetchData: (model, search) => (
                  model.fetch({
                    farm_id__eq: values.farm_id,
                    sort: 'started_at,desc|pond_name_alpha_numeric'
                  }).then((models) => models.filter((m) => (
                    `${m.pond_name} - ${moment(m.started_at).format("DD MMM YYYY")}${m.finished_at ? ` (${translate("Finished")})` : ""}`
                  ).toLowerCase().includes((search || '').toLowerCase())))
                ),
                renderSelectedItemText: (item) => item && `${item.pond_name} - ${moment(item.started_at).format("DD MMM YYYY")}${item.finished_at ? ` (${translate("Finished")})` : ""}`,
                clearable: true,
                isDisabled: !values.farm_id || !!initialValues.cycle_id,
                titleTx: "cycleDiseaseForm.cycleIdPlaceholder"
              }}
            />
            <DateTimePickerField
              name="logged_date"
              txPrefix="cycleDiseaseForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
            />
            <ModelField
              name="disease_id"
              txPrefix="cycleDiseaseForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: !!values.disease_id }}
              modelPickerProps={{
                Model: Disease,
                dataLabel: "full_name",
                titleTx: "cycleDiseaseForm.diseaseIdPlaceholder",
                fetchData: (model, search) => (
                  model.fetch({ search, sort: "full_name" })
                    .then((models) => {
                      return [
                      ...models,
                      { id: null, full_name: translate('cycleDiseaseForm.otherDisease') }
                    ]})
                ),
                onValueChange: async (value) => {
                  await setFieldValue("other_disease", null)
                  await setFieldValue("disease_id", value || null)
                  await setFieldTouched("disease_id")
                },
                pickerActionsheetProps: {
                  renderItem({ item }) {
                    if (!item) return null;
                    return <View paddingY="12px" paddingX="16px">
                      <Text text={item ? item.short_name || item.full_name : translate("cycleDiseaseForm.diseaseIdPlaceholder")} preset="title4" marginBottom="4px" colorScheme={colorScheme} />
                      {item && !!item.short_name && <Text text={item.full_name} preset="body2" colorScheme={colorScheme} />}
                    </View>
                  },
                  flatListProps: {
                    keyExtractor: (item) => item.id || 'null'
                  }
                },
                get renderSelectedItem() {
                  return (item) => this.pickerActionsheetProps.renderItem({ item } as any)
                },
              }}
            />
            {values.disease_id === null && <TextField
              name="other_disease"
              txPrefix="cycleDiseaseForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
              formControlProps={{ isRequired: true }}
            />}
            <WalkthroughElement
              id="cycle-disease-form-second"
              scrollViewRef={scrollViewRef}
              scrollOffset={250}
            >
              <View style={WRAPPER_IMAGE}>
                <MultipleImageField
                  name="files"
                  txPrefix="cycleDiseaseForm"
                  colorScheme={colorScheme}
                />
              </View>
            </WalkthroughElement>
            <TextField
              name="remark"
              txPrefix="cycleDiseaseForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
              formControlProps={{ inputProps: { multiline: true, numberOfLines: 3, onSubmitEditing: undefined } }}
            />
          </WalkthroughElement>
        </>
      )}
    </Formik>
  )
})

CycleDiseaseForm.displayName = 'CycleDiseaseForm'
